import {ServerWaysWinChecker} from '@fantasmagames/prototype/src/win/ServerWaysWinChecker';
import {ServerSymbolPosition, ServerWinLine} from '@fantasmagames/prototype/src/win/ServerWinLine';
import {CurrentReelsCombination, Reelsets, ServerAvalancheReels} from '@fantasmagames/prototype/src/reels/ServerAvalancheReels';
import {ServerRandomUtils} from '@fantasmagames/prototype/src/utils/ServerRandomUtils';
import {
	BA,
	BB,
	BC,
	CHEST_EXPLODE,
	CHEST_RESPIN,
	CHEST_WILD,
	E,
	H1,
	H2,
	H3,
	H4,
	H5,
	KEY,
	L1,
	L2,
	L3,
	L4,
	MIMIC_CHEST,
	SC,
	W,
} from '@fantasmagames/prototype/src/symbols/ServerSymbols';
import {ServerData, ServerGameFeatures} from '@fantasmagames/prototype/src/data/ServerData';
import {ServerCheats} from '@fantasmagames/prototype/src/cheats/ServerCheats';

export interface AvalancheRound {
	reelsCombination: CurrentReelsCombination;
	newSymbols: number[][];
}

export interface ExpandedWildData {
	wildPosition: ServerSymbolPosition;
	multiplier: number;
}

export interface RespinData {
	respinSymbolPosition: ServerSymbolPosition;
	otherSymbolsToRemove: ServerSymbolPosition[];
}

export interface ExplosionData {
	explosionCenter: ServerSymbolPosition;
	explosionPositions: ServerSymbolPosition[];
}

export interface InsideOfChest {
	hasMimicFreeSpins: boolean;
	hasExpandedWild: boolean;
	hasRespin: boolean;
	hasExplotion: boolean;
}

export enum ServerEvents {
	SPIN = 0,
	AVALANCHE = 1,
	WILD_EXPANDING = 2,
	RESPIN = 3,
	EXPLOSION = 4,
	MIMIC_FS = 5,
	BATTLE_FS = 6,
	FREE_SPIN = 7,
}

export enum CurrentGameState {
	BASE_GAME = 0,
	MIMIC_FREE_SPIN = 1,
	FREE_SPINS = 2,
}

export const GAME_REELS: number = 6;
export const GAME_ROWS: number = 6;

export class ServerAvalancheReelsRound {
	protected _initialCombination: CurrentReelsCombination;
	protected _currentCombination: CurrentReelsCombination;
	protected _rounds: AvalancheRound[] = [];
	// [round index][win lines of that round]
	protected _winLines: ServerWinLine[][] = [];
	protected _gameEvents: ServerEvents[] = [];

	protected _currentGameState: CurrentGameState = 0;
	protected _reelsWithFeatureSymbols: number[] = [];

	protected _currentReels: ServerAvalancheReels;
	protected _winChecker: ServerWaysWinChecker;

	protected _hasChest: boolean;
	protected _insideChest: InsideOfChest;
	protected _chestPosition: ServerSymbolPosition;

	protected _hasRandomMultiplier: boolean;
	protected _randomMultiplier: number = 1;

	protected _avalancheMultiplier: number = 1;

	protected _hasExpandedWilds: boolean = false;
	protected _expandedWilds: ExpandedWildData[] = [];

	protected _hasRespins: boolean = false;
	protected _respins: RespinData[] = [];

	protected _hasExplosions: boolean = false;
	protected _explosions: ExplosionData[] = [];

	protected _currentServerData: ServerData;
	protected _featuresOffset: number[] = [0, 0, 0];

	protected _isMimicFSRound: boolean = false;
	protected _isFreeSpinRound: boolean = false;
	protected _currentTotalFreeSpins: number = 0;
	protected _currentFreeSpinsPlayed: number = 0;

	protected _keysInRound: number = 0;
	private _additionalFSInThisRound: number = 0;

	protected initialize(): void {
		this._currentReels = new ServerAvalancheReels();
		this._winChecker = new ServerWaysWinChecker();

		this._hasChest = false;
		this._insideChest = {hasExpandedWild: false, hasExplotion: false, hasMimicFreeSpins: false, hasRespin: false};
	}

	public spin(serverData: ServerData, cheats: ServerCheats): void {
		this.initialize();

		if (cheats.isCheatsEnabled && cheats.shouldTriggerBattleFreeSpins) {
			serverData.activateFeature(ServerGameFeatures.RESPIN);
			serverData.activateFeature(ServerGameFeatures.EXPANDED_WILD);
			serverData.activateFeature(ServerGameFeatures.EXPLOSION);
		}

		this._currentServerData = serverData;
		let nextReelset: Reelsets = Reelsets.BASIC;

		// in mimic free spins there is a 20% chance to take reels from MIMIC_FREE_SPIN Reelset
		if (serverData.isInMimicFreeSpins) {
			const mimicReelsChance: number = ServerRandomUtils.randomInt(1, 100);
			if (mimicReelsChance <= 20) {
				nextReelset = Reelsets.MIMIC_FS;
			}
		}

		// generating initial combination
		this._initialCombination = this._currentReels.generateNewCombination(nextReelset, cheats);
		console.log(this._initialCombination.visibleReelsPositions);
		console.log(this._initialCombination.visibleReels);

		// if we're in basic game
		if (!serverData.isInMimicFreeSpins && !serverData.isInFreeSpins) {
			// if 3 features are unlocked
			if (serverData.activeFeaturesNumber >= 3) {
				// adding keys symbols
				this.addKeysSymbols(this._initialCombination, cheats);
			} else {
				// adding chests
				this.addChest(this._initialCombination, serverData, cheats);
			}
		}

		this._initialCombination = this._currentReels.tryToAddMegablocks(this._initialCombination);

		let currentActiveFeatures: boolean[] = serverData.featuresActiveStatus;
		if (serverData.isInMimicFreeSpins || serverData.isInFreeSpins) {
			currentActiveFeatures = serverData.FSFeaturesActiveStatus;
		}

		// adding feature symbols
		this._reelsWithFeatureSymbols = [];
		for (let i = 0; i < 3; i++) {
			if (currentActiveFeatures[i]) {
				this.addFeatureSymbol(this._initialCombination, this._currentGameState, currentActiveFeatures, E + i, cheats);
			}
		}

		if (serverData.isInFreeSpins) {
			// TODO: if a feature removes a beast symbol +1 FS is awarded (not in math)
			this.addBeastSymbols(this._initialCombination);
		}

		this.addRandomMultiplier(serverData.isInFreeSpins, serverData.isInMimicFreeSpins);

		this._gameEvents.push(ServerEvents.SPIN);

		let winLines: ServerWinLine[] = this._winChecker.findWins(this._initialCombination.visibleReels);
		this._currentCombination = this.makeCopyOfCurrentReelsCombination(this._initialCombination);

		this.applyFeatures(this._currentCombination, serverData, winLines, cheats);
		winLines = this._winChecker.findWins(this._currentCombination.visibleReels);

		while (winLines.length > 0) {
			this._winLines.push(winLines);
			this._currentReels.killWinLinesSymbols(this._currentCombination, winLines);
			this._currentReels.dropDownSymbols(this._currentCombination);

			const newSymbols: number[][] = this._currentReels.addNewSymbols(
				this._currentCombination,
				Reelsets.BASIC,
				this._randomMultiplier,
				this._avalancheMultiplier,
			);
			this._rounds.push({
				newSymbols,
				reelsCombination: this.makeCopyOfCurrentReelsCombination(this._currentCombination),
			});

			winLines = this._winChecker.findWins(this._currentCombination.visibleReels);

			this._gameEvents.push(ServerEvents.AVALANCHE);
		}

		if (serverData.isInMimicFreeSpins) {
			this._isMimicFSRound = true;
			serverData.makeMimicFreeSpin();
			this._currentTotalFreeSpins = serverData.totalMimcFreeSpin;
			this._currentFreeSpinsPlayed = serverData.mimicFreeSpinsPlayed;
		}

		if (serverData.isInFreeSpins) {
			this._isFreeSpinRound = true;
			serverData.makeFreeSpin();
			this._currentTotalFreeSpins = serverData.totalFreeSpins;
			this._currentFreeSpinsPlayed = serverData.freeSpinsPlayed;
		}

		if (this._insideChest.hasMimicFreeSpins) {
			this._currentGameState = CurrentGameState.MIMIC_FREE_SPIN;
			serverData.startMimicFreeSpins(5);
			this._currentTotalFreeSpins = serverData.totalMimcFreeSpin;
			this._currentFreeSpinsPlayed = serverData.mimicFreeSpinsPlayed;
			this._gameEvents.push(ServerEvents.MIMIC_FS);
		}

		if (this._keysInRound >= 3) {
			let totalFS: number = 5;
			if (this._keysInRound === 4) {
				totalFS = 6;
			}
			if (this._keysInRound === 5) {
				totalFS = 8;
			}
			if (this._keysInRound === 6) {
				totalFS = 10;
			}
			this._currentGameState = CurrentGameState.FREE_SPINS;
			serverData.startFreeSpins(totalFS);
			this._currentTotalFreeSpins = totalFS;
			this._currentFreeSpinsPlayed = 0;
			this._gameEvents.push(ServerEvents.BATTLE_FS);
		}
	}

	protected applyFeatures(
		round: CurrentReelsCombination,
		server: ServerData,
		winLines: ServerWinLine[],
		cheats: ServerCheats,
	): CurrentReelsCombination {
		let currentActiveFeatures: boolean[] = server.featuresActiveStatus;
		if (server.isInMimicFreeSpins) {
			currentActiveFeatures = server.FSFeaturesActiveStatus;
		}

		// has expanded wild feature active
		if (currentActiveFeatures[ServerGameFeatures.EXPANDED_WILD]) {
			let wilds: ServerSymbolPosition[] = this.getWildsOnReels(round);
			if (cheats.isCheatsEnabled && cheats.expandedWildReels.length > 0) {
				wilds = [];
				for (const reelIndex of cheats.expandedWildReels) {
					wilds.push({reel: reelIndex, row: 0});
				}
			}
			if (wilds && wilds.length > 0) {
				this.applyFeatureExpandedWild(wilds, round);
				this._gameEvents.push(ServerEvents.WILD_EXPANDING);

				this._rounds.push({
					newSymbols: [[], [], [], [], [], []],
					reelsCombination: this.makeCopyOfCurrentReelsCombination(round),
				});
			}
		}

		// has explode feature active
		// TODO: should always try to add megablock instead (if possible)
		if (currentActiveFeatures[ServerGameFeatures.EXPLOSION]) {
			const explodes: ServerSymbolPosition[] = this.getExplodeSymbols(round);
			if (explodes && explodes.length > 0) {
				this.applyFeatureExplode(explodes, round);
				this._gameEvents.push(ServerEvents.EXPLOSION);

				this._currentReels.dropDownSymbols(this._currentCombination);

				const newSymbols: number[][] = this._currentReels.addNewSymbols(
					this._currentCombination,
					Reelsets.BASIC,
					this._randomMultiplier,
					this._avalancheMultiplier,
				);

				this._rounds.push({
					newSymbols,
					reelsCombination: this.makeCopyOfCurrentReelsCombination(round),
				});
			}
		}

		// has respin feature active
		if (currentActiveFeatures[ServerGameFeatures.RESPIN]) {
			const respins: ServerSymbolPosition[] = this.getRespinSymbols(round);
			if (respins && respins.length > 0) {
				this.applyFeatureRespin(respins, round);
				this._gameEvents.push(ServerEvents.RESPIN);

				this._currentReels.dropDownSymbols(this._currentCombination);

				const newSymbols: number[][] = this._currentReels.addNewSymbols(
					this._currentCombination,
					Reelsets.BASIC,
					this._randomMultiplier,
					this._avalancheMultiplier,
				);

				this._rounds.push({
					newSymbols,
					reelsCombination: this.makeCopyOfCurrentReelsCombination(round),
				});
			}
		}

		return round;
	}

	protected getExplodeSymbols(round: CurrentReelsCombination): ServerSymbolPosition[] {
		const explodes: ServerSymbolPosition[] = [];

		for (let reelIndex: number = 0; reelIndex < round.visibleReels.length; reelIndex++) {
			for (let rowIndex: number = 0; rowIndex < round.visibleReels[reelIndex].length; rowIndex++) {
				if (round.visibleReels[reelIndex][rowIndex] === E || round.visibleReels[reelIndex][rowIndex] === CHEST_EXPLODE) {
					explodes.push({reel: reelIndex, row: rowIndex});
				}
			}
		}

		return explodes;
	}

	protected getRespinSymbols(round: CurrentReelsCombination): ServerSymbolPosition[] {
		const respins: ServerSymbolPosition[] = [];

		for (let reelIndex: number = 0; reelIndex < round.visibleReels.length; reelIndex++) {
			for (let rowIndex: number = 0; rowIndex < round.visibleReels[reelIndex].length; rowIndex++) {
				if (round.visibleReels[reelIndex][rowIndex] === SC || round.visibleReels[reelIndex][rowIndex] === CHEST_RESPIN) {
					respins.push({reel: reelIndex, row: rowIndex});
				}
			}
		}

		return respins;
	}

	protected getWildsOnReels(round: CurrentReelsCombination): ServerSymbolPosition[] {
		const wilds: ServerSymbolPosition[] = [];

		for (let reelIndex: number = 0; reelIndex < round.visibleReels.length; reelIndex++) {
			for (let rowIndex: number = 0; rowIndex < round.visibleReels[reelIndex].length; rowIndex++) {
				if (round.visibleReels[reelIndex][rowIndex] === W || round.visibleReels[reelIndex][rowIndex] === CHEST_WILD) {
					// assuming there can't be 2 wilds on the same reel
					wilds.push({reel: reelIndex, row: rowIndex});
				}
			}
		}

		return wilds;
	}

	protected addBeastSymbols(round: CurrentReelsCombination): CurrentReelsCombination {
		for (let reelIndex: number = 0; reelIndex < round.visibleReels.length; reelIndex++) {
			for (let rowIndex: number = 0; rowIndex < round.visibleReels[reelIndex].length; rowIndex++) {
				if (round.visibleReels[reelIndex][rowIndex] === L4) {
					round.visibleReels[reelIndex][rowIndex] = BA;
				}

				if (round.visibleReels[reelIndex][rowIndex] === L3) {
					round.visibleReels[reelIndex][rowIndex] = BB;
				}

				if (round.visibleReels[reelIndex][rowIndex] === L2) {
					round.visibleReels[reelIndex][rowIndex] = BC;
				}
			}
		}

		return round;
	}

	protected addRandomMultiplier(isInFreeSpins: boolean, isInMimicFreeSpins: boolean): void {
		this._hasRandomMultiplier = false;
		this._randomMultiplier = 1;
		if (!isInFreeSpins && !isInMimicFreeSpins) {
			const chance: number = ServerRandomUtils.randomInt(1, 100);
			if (chance <= 2) {
				this._hasRandomMultiplier = true;
				this._randomMultiplier = this.generateMultiplier();
			}
		} else {
			if (!isInMimicFreeSpins) {
				const chance: number = ServerRandomUtils.randomInt(1, 100);
				if (chance <= 30) {
					this._hasRandomMultiplier = true;
					this._randomMultiplier = this.generateMultiplier();
				}
			}
		}

		if (this._hasRandomMultiplier && this._randomMultiplier > 4) {
			this._randomMultiplier = this.generateMultiplier();
		}
	}

	protected generateMultiplier(): number {
		const values: number[] = [
			10,
			9,
			9,
			8,
			8,
			8,
			7,
			7,
			7,
			7,
			6,
			6,
			6,
			6,
			6,
			5,
			5,
			5,
			5,
			5,
			5,
			5,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			4,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			3,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
			2,
		];

		return values[ServerRandomUtils.randomInt(0, values.length - 1)];
	}

	/**
	 * should be called in base game only if all 3 characters are opened
	 * @param round - current round
	 */
	protected addKeysSymbols(round: CurrentReelsCombination, cheats: ServerCheats): CurrentReelsCombination {
		if (cheats.shouldTriggerBattleFreeSpins) {
			round.visibleReels[1][ServerRandomUtils.randomInt(0, 5)] = KEY;
			round.visibleReels[3][ServerRandomUtils.randomInt(0, 5)] = KEY;
			round.visibleReels[5][ServerRandomUtils.randomInt(0, 5)] = KEY;
			this._keysInRound = 3;
		} else {
			const randomNumber1: number = ServerRandomUtils.randomInt(0, 9999);

			if (randomNumber1 < 80) {
				round.visibleReels[1][0] = KEY;
				round.visibleReels[1][5] = KEY;
				this._keysInRound += 2;
			} else if (randomNumber1 < 1400) {
				round.visibleReels[1][ServerRandomUtils.randomInt(0, 5)] = KEY;
				this._keysInRound++;
			}

			const randomNumber2: number = ServerRandomUtils.randomInt(0, 9999);

			if (randomNumber2 < 80) {
				round.visibleReels[3][0] = KEY;
				round.visibleReels[3][5] = KEY;
				this._keysInRound += 2;
			} else if (randomNumber2 < 1400) {
				round.visibleReels[3][ServerRandomUtils.randomInt(0, 5)] = KEY;
				this._keysInRound++;
			}

			const randomNumber3: number = ServerRandomUtils.randomInt(0, 9999);

			if (randomNumber3 < 80) {
				round.visibleReels[5][0] = KEY;
				round.visibleReels[5][5] = KEY;
				this._keysInRound += 2;
			} else if (randomNumber3 < 1400) {
				round.visibleReels[5][ServerRandomUtils.randomInt(0, 5)] = KEY;
				this._keysInRound++;
			}
		}

		return round;
	}

	/**
	 * only in base game and if not all features unlocked
	 */
	protected addChest(round: CurrentReelsCombination, data: ServerData, cheats: ServerCheats): CurrentReelsCombination {
		const randomChance: number = ServerRandomUtils.randomInt(1, 100);
		if (randomChance <= 24 || (cheats.isCheatsEnabled && cheats.shouldAddChest)) {
			const positionX: number = ServerRandomUtils.randomInt(1, 5);
			const positionY: number = ServerRandomUtils.randomInt(0, 5);

			this.openChest(
				round,
				{reel: positionX, row: positionY},
				data.priorityFeatureIndex,
				data.nonPriorityFeatureIndex,
				data.initiallyUnlockedFeatureIndex,
				cheats,
			);
		}

		return round;
	}

	protected openChest(
		round: CurrentReelsCombination,
		position: ServerSymbolPosition,
		priorityFeatureIndex: number,
		nonPriorityFeatureIndex: number,
		initialFeatureIndex: number,
		cheats: ServerCheats,
	): CurrentReelsCombination {
		let mimicFSChance: number = ServerRandomUtils.randomInt(1, 100);
		this._hasChest = true;
		this._chestPosition = {reel: position.reel, row: position.row};
		if (cheats.shouldNOTWinMimicFS) {
			mimicFSChance = 13;
		}
		if (mimicFSChance <= 12 || (cheats.isCheatsEnabled && cheats.chestContent.hasMimicFreeSpins)) {
			this._insideChest.hasMimicFreeSpins = true;
			round.visibleReels[position.reel][position.row] = MIMIC_CHEST;
		} else {
			if (
				cheats.isCheatsEnabled &&
				(cheats.chestContent.hasExpandedWild || cheats.chestContent.hasRespin || cheats.chestContent.hasExplotion)
			) {
				if (cheats.chestContent.hasExpandedWild) {
					this.applyChest(ServerGameFeatures.EXPANDED_WILD, round, position);
					this._insideChest.hasExpandedWild = true;
				}
				if (cheats.chestContent.hasExplotion) {
					this.applyChest(ServerGameFeatures.EXPLOSION, round, position);
					this._insideChest.hasExplotion = true;
				}
				if (cheats.chestContent.hasRespin) {
					this.applyChest(ServerGameFeatures.RESPIN, round, position);
					this._insideChest.hasRespin = true;
				}

				return round;
			}
			let feature: ServerGameFeatures;
			// there are 2 features opened
			if (nonPriorityFeatureIndex < 0) {
				feature = ServerRandomUtils.randomInt(0, 2);
				round.visibleReels[position.reel][position.row] = this.getFeatureSymbol(feature);
			} else {
				const featureChestChance: number = ServerRandomUtils.randomInt(1, 100);
				if (featureChestChance <= 14) {
					feature = nonPriorityFeatureIndex;
					this.applyChest(nonPriorityFeatureIndex, round, position);
				} else if (featureChestChance < 40) {
					feature = initialFeatureIndex;
					this.applyChest(initialFeatureIndex, round, position);
				} else {
					feature = priorityFeatureIndex;
					this.applyChest(priorityFeatureIndex, round, position);
				}
			}

			switch (feature) {
				case ServerGameFeatures.EXPANDED_WILD:
					this._insideChest.hasExpandedWild = true;
					break;
				case ServerGameFeatures.RESPIN:
					this._insideChest.hasRespin = true;
					break;
				case ServerGameFeatures.EXPLOSION:
					this._insideChest.hasExplotion = true;
					break;
				default:
					throw new Error('openChest impossible feature - ' + feature);
			}
		}

		return round;
	}

	protected applyChest(feature: number, round: CurrentReelsCombination, position: ServerSymbolPosition): void {
		if (!this._currentServerData.featuresActiveStatus[feature]) {
			round.visibleReels[position.reel][position.row] = this.getFeatureSymbol(feature);
			this._currentServerData.setLevelValue(feature, this._currentServerData.featuresLevels[feature] + 1);
			this._featuresOffset[feature]++;
			if (this._currentServerData.featuresLevels[feature] >= 5) {
				this._currentServerData.activateFeature(feature);
			}
		}
	}

	protected getFeatureSymbol(feature: ServerGameFeatures): number {
		switch (feature) {
			case ServerGameFeatures.EXPANDED_WILD:
				return CHEST_WILD;
			case ServerGameFeatures.EXPLOSION:
				return CHEST_EXPLODE;
			case ServerGameFeatures.RESPIN:
				return CHEST_RESPIN;
			default:
				throw new Error('getFeatureSymbol invalid feature - ' + feature);
		}
	}

	protected addFeatureSymbol(
		round: CurrentReelsCombination,
		currentGameState: CurrentGameState,
		activeFeatures: boolean[],
		symbol: number,
		cheats: ServerCheats,
	): CurrentReelsCombination {
		let canBeAwarded: number = 1;
		const chanceForIncrease: number = ServerRandomUtils.randomInt(1, 100);
		if (chanceForIncrease <= 5) {
			canBeAwarded++;
		}

		if (currentGameState === CurrentGameState.FREE_SPINS) {
			const freeGamesChanceForIncrease: number = ServerRandomUtils.randomInt(1, 1000);
			if (freeGamesChanceForIncrease <= 125) {
				canBeAwarded++;
			}
		}

		let featuresUnlocked: number = 0;
		for (const isFeatureUnlocked of activeFeatures) {
			if (isFeatureUnlocked) {
				featuresUnlocked++;
			}
		}

		if (featuresUnlocked === 0) {
			throw new Error('ServerAvalancheReelsRound::addExpandedWildSymbols featuresUnlocked cant be 0');
		}

		let featuresAwarded: number = 0;
		for (let i = 0; i < canBeAwarded; i++) {
			const featureAwardChance: number = 20 / featuresUnlocked;
			const featureAwardRandom: number = ServerRandomUtils.randomInt(1, 100);
			if (featureAwardChance <= featureAwardRandom) {
				featuresAwarded++;
			}
		}

		for (const ew of this._expandedWilds) {
			this._reelsWithFeatureSymbols.push(ew.wildPosition.reel);
		}

		if (this._hasChest) {
			this._reelsWithFeatureSymbols.push(this._chestPosition.reel);
		}
		for (let featureIndex = 0; featureIndex < featuresAwarded; featureIndex++) {
			if (this._reelsWithFeatureSymbols.length < 6) {
				if (
					cheats.isCheatsEnabled &&
					symbol === W &&
					cheats.expandedWildReels.length > 0 &&
					featureIndex < cheats.expandedWildReels.length
				) {
					const featureReelIndex: number = cheats.expandedWildReels[featureIndex];
					this._reelsWithFeatureSymbols.push(featureReelIndex);
					round.visibleReels[featureReelIndex][ServerRandomUtils.randomInt(0, 5)] = symbol;
				} else {
					// feature symbol should be generated on a reel with no other feature symbols
					const featureReelIndex: number = ServerRandomUtils.randomIntValueWithExceptions(0, 5, this._reelsWithFeatureSymbols);
					this._reelsWithFeatureSymbols.push(featureReelIndex);
					let foundGoodPosition: boolean = false;
					while (!foundGoodPosition) {
						// we should
						const posReel: number = ServerRandomUtils.randomInt(0, 5);
						if (round.visibleReels[featureReelIndex][posReel] !== KEY) {
							foundGoodPosition = true;
							round.visibleReels[featureReelIndex][posReel] = symbol;
						}
					}
				}
			}
		}

		return round;
	}

	protected applyFeatureExpandedWild(wildsPositions: ServerSymbolPosition[], round: CurrentReelsCombination): CurrentReelsCombination {
		this._hasExpandedWilds = wildsPositions.length > 0;
		this._expandedWilds = [];
		const multipliers: number[] = [9, 8, 7, 6, 5, 5, 5, 5, 4, 4, 4, 4, 4, 3, 3, 3, 3, 3, 3, 2, 2, 2, 2, 2, 2, 2, 2];
		for (const wildPos of wildsPositions) {
			round.visibleReels[wildPos.reel][0] = W;
			for (let rowIndex: number = 1; rowIndex < round.visibleReels[wildPos.reel].length; rowIndex++) {
				if (
					round.visibleReels[wildPos.reel][rowIndex] === BA ||
					round.visibleReels[wildPos.reel][rowIndex] === BB ||
					round.visibleReels[wildPos.reel][rowIndex] === BC
				) {
					this._additionalFSInThisRound++;
				}
				round.visibleReels[wildPos.reel][rowIndex] = -2;
			}

			if (!this.hasExpandedWildOnReel(wildPos.reel)) {
				this._expandedWilds.push({
					wildPosition: {reel: wildPos.reel, row: wildPos.row},
					multiplier: multipliers[ServerRandomUtils.randomInt(0, multipliers.length - 1)],
				});
			}
		}

		return round;
	}

	protected hasExpandedWildOnReel(reelIndex: number): boolean {
		for (const ew of this._expandedWilds) {
			if (ew.wildPosition.reel === reelIndex) {
				return true;
			}
		}

		return false;
	}

	protected applyFeatureRespin(scatters: ServerSymbolPosition[], round: CurrentReelsCombination): CurrentReelsCombination {
		this._hasRespins = scatters.length > 0;
		this._respins = [];

		const nonWinningPositions: ServerSymbolPosition[] = this._currentReels.getNonWinningSymolZzList(round, this._expandedWilds);
		const positionToRemove: ServerSymbolPosition[] = [];
		// removing all respin symbols positions from positions list
		// so that every respin symbol will remove at least itself
		for (const scatter of scatters) {
			positionToRemove.push({reel: scatter.reel, row: scatter.row});
		}

		for (const posToRemove of positionToRemove) {
			nonWinningPositions.forEach((item: ServerSymbolPosition, index: number) => {
				if (item.reel === posToRemove.reel && item.row === posToRemove.row) {
					nonWinningPositions.splice(index, 1);
				}
			});
		}

		for (const scatter of scatters) {
			const respinData: RespinData = {respinSymbolPosition: scatter, otherSymbolsToRemove: []};
			const symbolsToRemoveAmount: number =
				Math.abs(
					ServerRandomUtils.randomInt(0, 12) + ServerRandomUtils.randomInt(0, 11) + ServerRandomUtils.randomInt(0, 11) - 17,
				) + 2;

			round.visibleReels[scatter.reel][scatter.row] = -1;

			let numberOfSymbolsToRespin: number =
				symbolsToRemoveAmount <= nonWinningPositions.length ? symbolsToRemoveAmount : nonWinningPositions.length;

			while (numberOfSymbolsToRespin > 0) {
				const randomIndex: number = ServerRandomUtils.randomInt(0, nonWinningPositions.length - 1);
				const respinPos: ServerSymbolPosition = {
					reel: nonWinningPositions[randomIndex].reel,
					row: nonWinningPositions[randomIndex].row,
				};
				if (round.visibleReels[respinPos.reel][respinPos.row] !== SC && round.visibleReels[respinPos.reel][respinPos.row] !== E) {
					respinData.otherSymbolsToRemove.push(respinPos);
					if (
						round.visibleReels[respinPos.reel][respinPos.row] === BA ||
						round.visibleReels[respinPos.reel][respinPos.row] === BB ||
						round.visibleReels[respinPos.reel][respinPos.row] === BC
					) {
						this._additionalFSInThisRound++;
					}
					round.visibleReels[respinPos.reel][respinPos.row] = -1;
					nonWinningPositions.splice(randomIndex, 1);
					numberOfSymbolsToRespin--;
				}
			}

			this._respins.push(respinData);
		}

		return round;
	}

	protected applyFeatureExplode(
		explodeSymbolsPositions: ServerSymbolPosition[],
		round: CurrentReelsCombination,
	): CurrentReelsCombination {
		const var1Symbols: number[] = [L4, L3, L2, L1, H5, H4];
		const var2Symbols: number[] = [H3, H2, H1];

		this._hasExplosions = explodeSymbolsPositions.length > 0;
		this._explosions = [];

		const nonWinningPositions: ServerSymbolPosition[] = this._currentReels.getNonWinningSymolZzList(round, this._expandedWilds);

		for (const explodePos of explodeSymbolsPositions) {
			const explosion: ExplosionData = {explosionCenter: explodePos, explosionPositions: []};
			const awardedSymbolChance: number = ServerRandomUtils.randomInt(1, 100);

			let awardedSymbol: number;
			if (awardedSymbolChance <= 20) {
				awardedSymbol = var1Symbols[ServerRandomUtils.randomInt(0, var1Symbols.length - 1)];
			} else {
				awardedSymbol = var2Symbols[ServerRandomUtils.randomInt(0, var2Symbols.length - 1)];
			}

			const threeByThreeChance: number = ServerRandomUtils.randomInt(1, 100);
			// 3x3
			if (threeByThreeChance <= 20 && !this.isWallPosition(explodePos)) {
				explosion.explosionPositions.push({reel: explodePos.reel + 1, row: explodePos.row});
				explosion.explosionPositions.push({reel: explodePos.reel, row: explodePos.row + 1});
				explosion.explosionPositions.push({reel: explodePos.reel - 1, row: explodePos.row});
				explosion.explosionPositions.push({reel: explodePos.reel, row: explodePos.row - 1});
				explosion.explosionPositions.push({reel: explodePos.reel + 1, row: explodePos.row + 1});
				explosion.explosionPositions.push({reel: explodePos.reel - 1, row: explodePos.row - 1});
				explosion.explosionPositions.push({reel: explodePos.reel + 1, row: explodePos.row - 1});
				explosion.explosionPositions.push({reel: explodePos.reel - 1, row: explodePos.row + 1});
			} else {
				// 2x2
				if (!this.isWallPosition(explodePos)) {
					explosion.explosionPositions.push({reel: explodePos.reel - 1, row: explodePos.row - 1});
					explosion.explosionPositions.push({reel: explodePos.reel, row: explodePos.row - 1});
					explosion.explosionPositions.push({reel: explodePos.reel - 1, row: explodePos.row});
				} else {
					if (explodePos.reel < ServerAvalancheReels.REELS - 1 && explodePos.row === 0) {
						explosion.explosionPositions.push({reel: explodePos.reel + 1, row: explodePos.row + 1});
						explosion.explosionPositions.push({reel: explodePos.reel, row: explodePos.row + 1});
						explosion.explosionPositions.push({reel: explodePos.reel + 1, row: explodePos.row});
					} else if (explodePos.reel === ServerAvalancheReels.REELS - 1 && explodePos.row < ServerAvalancheReels.ROWS - 1) {
						explosion.explosionPositions.push({reel: explodePos.reel - 1, row: explodePos.row + 1});
						explosion.explosionPositions.push({reel: explodePos.reel, row: explodePos.row + 1});
						explosion.explosionPositions.push({reel: explodePos.reel - 1, row: explodePos.row});
					} else if (explodePos.reel > 0 && explodePos.row === ServerAvalancheReels.ROWS - 1) {
						explosion.explosionPositions.push({reel: explodePos.reel - 1, row: explodePos.row - 1});
						explosion.explosionPositions.push({reel: explodePos.reel, row: explodePos.row - 1});
						explosion.explosionPositions.push({reel: explodePos.reel - 1, row: explodePos.row});
					} else {
						explosion.explosionPositions.push({reel: explodePos.reel + 1, row: explodePos.row - 1});
						explosion.explosionPositions.push({reel: explodePos.reel, row: explodePos.row - 1});
						explosion.explosionPositions.push({reel: explodePos.reel + 1, row: explodePos.row});
					}
				}
			}

			for (let i = 0; i < explosion.explosionPositions.length; i++) {
				const pos: ServerSymbolPosition = explosion.explosionPositions[i];
				const index: number = nonWinningPositions.findIndex((winSymbol: ServerSymbolPosition) => {
					return pos.reel === winSymbol.reel && pos.row === winSymbol.row;
				});

				if (
					index >= 0 &&
					round.visibleReels[pos.reel][pos.row] !== W &&
					round.visibleReels[pos.reel][pos.row] !== -2 &&
					round.visibleReels[pos.reel][pos.row] !== E &&
					round.visibleReels[pos.reel][pos.row] !== SC &&
					round.visibleReels[pos.reel][pos.row] !== -1
				) {
					if (
						round.visibleReels[pos.reel][pos.row] === BA ||
						round.visibleReels[pos.reel][pos.row] === BB ||
						round.visibleReels[pos.reel][pos.row] === BC
					) {
						this._additionalFSInThisRound++;
					}
					round.visibleReels[pos.reel][pos.row] = -1;
				} else {
					explosion.explosionPositions.splice(i, 1);
					i--;
				}
			}
			this._explosions.push(explosion);
			round.visibleReels[explosion.explosionCenter.reel][explosion.explosionCenter.row] = -1;
		}

		return round;
	}

	protected isWallPosition(pos: ServerSymbolPosition): boolean {
		return pos.row === 0 || pos.row === ServerAvalancheReels.ROWS - 1 || pos.reel === 0 || pos.reel === ServerAvalancheReels.REELS - 1;
	}

	protected isCornerPosition(pos: ServerSymbolPosition): boolean {
		return (
			(pos.reel === 0 && pos.row === 0) ||
			(pos.reel === ServerAvalancheReels.REELS - 1 && pos.row === ServerAvalancheReels.ROWS - 1) ||
			(pos.reel === 0 && pos.row === ServerAvalancheReels.ROWS - 1) ||
			(pos.reel === ServerAvalancheReels.REELS - 1 && pos.row === 0)
		);
	}

	protected makeCopyOfCurrentReelsCombination(round: CurrentReelsCombination): CurrentReelsCombination {
		const visibleReelsPositions: number[] = round.visibleReelsPositions.slice();
		const visibleReels: number[][] = [];
		for (const reel of round.visibleReels) {
			visibleReels.push(reel.slice());
		}

		return {visibleReelsPositions, visibleReels};
	}

	public getCurrentRoundResult(): JSON {
		return JSON.parse(this.getCurrentRoundAsString());
	}

	public getCurrentRoundAsString(): string {
		let result: string = '{';

		// adding initial values
		result += '"initial": [';
		for (let reelIndex: number = 0; reelIndex < this._initialCombination.visibleReels.length; reelIndex++) {
			result += '[';
			for (let rowIndex: number = 0; rowIndex < this._initialCombination.visibleReels[reelIndex].length; rowIndex++) {
				result += `${this._initialCombination.visibleReels[reelIndex][rowIndex]}`;
				if (rowIndex !== this._initialCombination.visibleReels[reelIndex].length - 1) {
					result += ',';
				}
			}
			result += '],';
		}
		result = result.substring(0, result.length - 1);
		result += ']';

		if (this._rounds && this._rounds.length > 0) {
			result += ',"newsymbols": [';
			for (const round of this._rounds) {
				result += '[';
				for (let reelIndex = 0; reelIndex < round.newSymbols.length; reelIndex++) {
					result += '[';
					for (let rowIndex = 0; rowIndex < round.newSymbols[reelIndex].length; rowIndex++) {
						result += `${round.newSymbols[reelIndex][rowIndex]}`;
						if (rowIndex !== round.newSymbols[reelIndex].length - 1) {
							result += ',';
						}
					}
					result += '],';
				}
				result = result.substring(0, result.length - 1);
				result += '],';
			}
			result = result.substring(0, result.length - 1);
			result += '],';

			result += '"currentreels": [';
			for (const round of this._rounds) {
				result += '[';
				for (let reelIndex = 0; reelIndex < round.reelsCombination.visibleReels.length; reelIndex++) {
					result += '[';
					for (let rowIndex = 0; rowIndex < round.reelsCombination.visibleReels[reelIndex].length; rowIndex++) {
						result += `${round.reelsCombination.visibleReels[reelIndex][rowIndex]}`;
						if (rowIndex !== round.reelsCombination.visibleReels[reelIndex].length - 1) {
							result += ',';
						}
					}
					result += '],';
				}
				result = result.substring(0, result.length - 1);
				result += '],';
			}
			result = result.substring(0, result.length - 1);
			result += ']';
		}

		if (this._winLines.length > 0) {
			result += ',"wins":[';
			for (const roundWinLines of this._winLines) {
				result += '[';
				for (const wl of roundWinLines) {
					result += '{';
					result += '"positions": [';
					for (const ws of wl.winSymbols) {
						result += `{"reel":${ws.position.reel},"row":${ws.position.row},"symbol":${ws.symbol}},`;
					}
					result = result.substring(0, result.length - 1);
					result += '],';
					result += `"win": ${wl.win},`;
					result += `"mainsymbol": ${wl.mainWinSymbol}`;
					result += '},';
				}
				result = result.substring(0, result.length - 1);
				result += '],';
			}
			result = result.substring(0, result.length - 1);
			result += ']';
		}

		if (this._hasExpandedWilds) {
			result += ',"expandedWilds":[';
			for (const expandedWild of this._expandedWilds) {
				result += '{';
				result += `"multiplier":${expandedWild.multiplier},"reel":${expandedWild.wildPosition.reel},"row":${expandedWild.wildPosition.row}`;
				result += '},';
			}
			result = result.substring(0, result.length - 1);
			result += ']';
		}

		if (this._gameEvents.length > 0) {
			result += ',"events":[';
			for (const event of this._gameEvents) {
				result += `${event},`;
			}
			result = result.substring(0, result.length - 1);
			result += ']';
		}

		if (this._respins.length > 0) {
			result += ',"respins":[';
			for (const respin of this._respins) {
				result += '{';
				result += `"scatter": {"reel":${respin.respinSymbolPosition.reel},"row":${respin.respinSymbolPosition.row}},`;
				result += '"symbols":[';
				for (const pos of respin.otherSymbolsToRemove) {
					result += `{"reel":${pos.reel},"row":${pos.row}},`;
				}
				result = result.substring(0, result.length - 1);
				result += ']';
				result += '},';
			}
			result = result.substring(0, result.length - 1);
			result += ']';
		}

		if (this._explosions.length > 0) {
			result += ',"explosions":[';
			for (const explosion of this._explosions) {
				result += '{';
				result += `"explosionCenter": {"reel":${explosion.explosionCenter.reel},"row":${explosion.explosionCenter.row}},`;
				result += '"symbols":[';
				for (const pos of explosion.explosionPositions) {
					result += `{"reel":${pos.reel},"row":${pos.row}},`;
				}
				if (explosion.explosionPositions.length > 0) {
					result = result.substring(0, result.length - 1);
				}
				result += ']';
				result += '},';
			}
			result = result.substring(0, result.length - 1);
			result += ']';
		}

		if (this._isMimicFSRound) {
			result += ',"mimicFreeSpinsData":{';
			result += `"freeSpinsTotal":${this._currentTotalFreeSpins},`;
			result += `"freeSpinsPlayed":${this._currentFreeSpinsPlayed}`;
			result += '}';
		}

		if (this._isFreeSpinRound) {
			result += ',"freeSpinsData":{';
			result += `"freeSpinsTotal":${this._currentTotalFreeSpins},`;
			result += `"freeSpinsPlayed":${this._currentFreeSpinsPlayed}`;
			result += '}';
		}

		if (this._currentReels.currentMegablock && this._currentReels.currentMegablock.isFull) {
			result += ',"megablock":{';
			result += `"size":${this._currentReels.currentMegablock.size},`;
			result += `"position":{"reel":${this._currentReels.currentMegablock.position.reel},"row":${this._currentReels.currentMegablock.position.row}}`;
			result += '}';
		}

		result += ',"features":{';

		result += '"expanded_wild": {';
		result += `"current":${this._currentServerData.featuresLevels[ServerGameFeatures.EXPANDED_WILD]},"offset":${
			this._featuresOffset[ServerGameFeatures.EXPANDED_WILD]
		}`;
		result += '},';

		result += '"respin": {';
		result += `"current":${this._currentServerData.featuresLevels[ServerGameFeatures.RESPIN]},"offset":${
			this._featuresOffset[ServerGameFeatures.RESPIN]
		}`;
		result += '},';

		result += '"explosion": {';
		result += `"current":${this._currentServerData.featuresLevels[ServerGameFeatures.EXPLOSION]},"offset":${
			this._featuresOffset[ServerGameFeatures.EXPLOSION]
		}`;
		result += '}';

		result += '}';

		result += '}';

		console.log(result);

		return result;
	}

	protected printCombination(combination: CurrentReelsCombination): void {
		let finalData: string = '';
		finalData += 'positions: ' + combination.visibleReelsPositions.toString() + '\n\n';
		for (let rowIndex = 0; rowIndex < GAME_ROWS; rowIndex++) {
			for (let reelIndex = 0; reelIndex < GAME_REELS; reelIndex++) {
				finalData += combination.visibleReels[reelIndex][rowIndex] + ' ';
			}
			finalData += '\n';
		}

		console.log(finalData);
	}

	protected printWinLines(winLines: ServerWinLine[]): void {
		let finalData: string = '';
		for (const wl of winLines) {
			finalData += `win symbol: ${wl.mainWinSymbol} multiplier: ${wl.multiplier}\n`;
			for (const ws of wl.winSymbols) {
				finalData += `symbol: ${ws.symbol} reel: ${ws.position.reel} row: ${ws.position.row}\n`;
			}
			finalData += '\n';
		}
		console.log(finalData);
	}

	public get currentGameState(): CurrentGameState {
		return this._currentGameState;
	}

	public get additionalFSInThisRound(): number {
		return this._additionalFSInThisRound;
	}
}

(window as any).server = new ServerAvalancheReelsRound();



// WEBPACK FOOTER //
// ./@fantasmagames/prototype/src/reels/ServerAvalancheReelsRound.ts